import React from "react";
import { Button } from "./Button";
import {
  FaFileImage,
  FaFileVideo,
  FaCheck,
  FaTimes,
  FaRedo,
} from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { AlertCircle, Clock, Upload, CheckCircle, XCircle } from "lucide-react";
import VideoThumbnail from "../listings/VideoThumbnail";
import { FileUploadState, UploadStatus } from "../listings/types";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FaTrash } from "react-icons/fa6";

interface FileUploadCardProps {
  fileState: FileUploadState;
  onRemove: (fileId: string) => void;
  onRetry: (fileId: string) => void;
  onCancel: (fileId: string) => void;
  onShowProcessingOverlay?: () => void;
}

const FileUploadCard: React.FC<FileUploadCardProps> = ({
  fileState,
  onRemove,
  onRetry,
  onCancel,
  onShowProcessingOverlay,
}) => {
  const isMobile = useIsMobile(700);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const getStatusIcon = () => {
    switch (fileState.status) {
      case UploadStatus.PENDING:
        return <Clock className="h-4 w-4 text-gray-500" />;
      case UploadStatus.VALIDATING:
      case UploadStatus.GETTING_SIGNED_URL:
        return <AlertCircle className="h-4 w-4 text-secondary" />;
      case UploadStatus.UPLOADING:
      case UploadStatus.PROCESSING:
      case UploadStatus.PERSISTING:
        return <Upload className="h-4 w-4 text-secondary" />;
      case UploadStatus.COMPLETED:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case UploadStatus.FAILED:
        return <XCircle className="h-4 w-4 text-red-500" />;
      case UploadStatus.CANCELLED:
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (fileState.status) {
      case UploadStatus.PENDING:
        return "Waiting...";
      case UploadStatus.VALIDATING:
        return "Validating...";
      case UploadStatus.GETTING_SIGNED_URL:
        return "Preparing...";
      case UploadStatus.UPLOADING:
        return `Uploading... ${Math.round(fileState.progress)}%`;
      case UploadStatus.PROCESSING:
        return "Processing...";
      case UploadStatus.PERSISTING:
        return "Saving...";
      case UploadStatus.COMPLETED:
        return "Complete";
      case UploadStatus.FAILED:
        return "Failed";
      case UploadStatus.CANCELLED:
        return "Cancelled";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = () => {
    switch (fileState.status) {
      case UploadStatus.VALIDATING:
      case UploadStatus.GETTING_SIGNED_URL:
      case UploadStatus.UPLOADING:
      case UploadStatus.PROCESSING:
      case UploadStatus.PERSISTING:
        return "text-secondary";
      case UploadStatus.COMPLETED:
        return "text-green-600";
      case UploadStatus.FAILED:
        return "text-red-600";
      case UploadStatus.CANCELLED:
      case UploadStatus.PENDING:
      default:
        return "text-gray-600";
    }
  };

  const getProgressBarColor = () => {
    switch (fileState.status) {
      case UploadStatus.UPLOADING:
      case UploadStatus.PROCESSING:
      case UploadStatus.PERSISTING:
        return "bg-secondary";
      case UploadStatus.COMPLETED:
        return "bg-green-500";
      case UploadStatus.FAILED:
        return "bg-red-500";
      default:
        return "bg-gray-300";
    }
  };

  const canRetry =
    fileState.status === UploadStatus.FAILED && fileState.retryCount < 3;
  const canCancel = [
    UploadStatus.PENDING,
    UploadStatus.VALIDATING,
    UploadStatus.GETTING_SIGNED_URL,
    UploadStatus.UPLOADING,
    UploadStatus.PROCESSING,
  ].includes(fileState.status);
  const canRemove = [
    UploadStatus.COMPLETED,
    UploadStatus.FAILED,
    UploadStatus.CANCELLED,
  ].includes(fileState.status);

  return (
    <div
      className={`border rounded-lg p-3 bg-white shadow-sm overflow-hidden ${
        isMobile ? "mx-2" : ""
      }`}
    >
      {/* File Preview and Info */}
      <div className="flex items-start space-x-3 mb-3">
        {/* Preview */}
        <div className="flex-shrink-0 w-16 h-16 relative">
          {fileState.file.type.startsWith("image/") ? (
            <>
              <img
                src={fileState.preview}
                alt={fileState.file.name}
                className="w-full h-full object-cover border rounded-md"
              />
              <FaFileImage
                className="absolute bottom-0 right-0 text-secondary bg-white rounded-full p-0.5"
                size={16}
              />
            </>
          ) : (
            <>
              <VideoThumbnail
                videoUrl={fileState.preview}
                className="w-full h-full object-cover border rounded-md"
              />
              <FaFileVideo
                className="absolute bottom-0 right-0 text-secondary bg-white rounded-full p-0.5"
                size={16}
              />
            </>
          )}

          {/* Status overlay */}
          {fileState.status === UploadStatus.COMPLETED && (
            <div className="absolute inset-0 bg-black bg-opacity-20 rounded-md flex items-center justify-center">
              <FaCheck className="text-green-600" size={24} />
            </div>
          )}
          {fileState.status === UploadStatus.FAILED && (
            <div className="absolute inset-0 bg-red-500 bg-opacity-20 rounded-md flex items-center justify-center">
              <FaTimes className="text-red-600" size={24} />
            </div>
          )}
        </div>

        {/* File Info */}
        <div className="flex-grow min-w-0">
          <h4
            className="text-sm font-medium text-gray-900 truncate"
            title={fileState.file.name}
          >
            {isMobile && fileState.file.name.length > 12
              ? `${fileState.file.name.slice(0, 12)}...`
              : fileState.file.name}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatFileSize(fileState.file.size)}
            {fileState.needsConversion && (
              <span
                className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-secondary text-primary cursor-pointer hover:bg-primary hover:text-secondary"
                onClick={() => onShowProcessingOverlay?.()}
                role="button"
                title="Show processing details"
              >
                Needs Processing
              </span>
            )}
          </p>

          {/* Status */}
          <div className="flex items-center mt-1">
            {getStatusIcon()}
            <span className={`text-xs ml-1 ${getStatusColor()}`}>
              {getStatusText()}
            </span>
          </div>

          {/* Error message */}
          {fileState.error && (
            <p className="text-xs text-red-600 mt-1" title={fileState.error}>
              {fileState.error}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-start space-x-1">
          {canCancel && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
              onClick={() => onCancel(fileState.id)}
              title="Cancel upload"
            >
              <ImCross size={12} />
            </Button>
          )}
          {canRetry && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-secondary hover:text-blue-700"
              onClick={() => onRetry(fileState.id)}
              title="Retry upload"
            >
              <FaRedo size={12} />
            </Button>
          )}
          {(canRemove || fileState.status === UploadStatus.PENDING) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
              onClick={() => onRemove(fileState.id)}
              title="Remove file"
            >
              <FaTrash size={10} />
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {(fileState.status === UploadStatus.UPLOADING ||
        fileState.status === UploadStatus.PROCESSING ||
        fileState.status === UploadStatus.PERSISTING) && (
        <div className="mb-2">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>{Math.round(fileState.progress)}%</span>
            <span>
              {formatFileSize(fileState.uploadedBytes || 0)} /{" "}
              {formatFileSize(fileState.totalBytes || 0)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
              style={{ width: `${fileState.progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadCard;
